import { RX_COLLECTION_BULK_INSERT_CONTEXT, type RxChangeEventBulk, type RxCollection } from 'rxdb';
import type { RxReplicationState } from 'rxdb/plugins/replication';
import type { Subscription } from 'rxjs';
import { xor } from 'lodash';

import { isArchived, isDeleted, type Tag } from '@flintmint/core-shared/rxdb';

import { updateTagNumDocs } from './tag-handler';

/** Collections whose documents carry `tagIds` and therefore contribute to `tags.numDocs`. */
export const TAGGED_COLLECTIONS = ['notes', 'blocks', 'userSources', 'instructions'];

// bulkWrite contexts that count as "a change this device made to its own local state", including
// the master-wins rollback of this device's fork on a push conflict. An allowlist, so a future
// internal write context defaults to "not counted" — see docs/tag-counts/tag-counts-architecture.md.
const PUSH_CONFLICT_ROLLBACK_CONTEXT = 'replication-up-write-conflict';
const LOCAL_WRITE_CONTEXTS = [
  RX_COLLECTION_BULK_INSERT_CONTEXT,
  'incremental-write',
  'rx-document-save-data',
  'rx-collection-bulk-remove',
  PUSH_CONFLICT_ROLLBACK_CONTEXT,
];

// Per collection: document id -> the tags owned by the master state this device's fork assumed
// when its push came back a conflict. See recordPushConflictsForTagCounts.
const assumedTagIdsByCollection = new WeakMap<object, Map<string, string[]>>();

/**
 * Maintains `tags.numDocs` from a tagged collection's committed change stream instead of from the
 * write hooks (which re-run on a storage conflict and double-count). See docs/tag-counts/tag-counts-architecture.md.
 * @param rxCollection - tagged RxCollection to watch
 * @param tagsCollection - tags RxCollection the counts are written to
 * @param trackWrite - called synchronously with each issued count write so the caller can hold
 * "awaiting sync" open until it settles; the write itself stays unawaited in the stream
 */
export const subscribeTagCountsToLocalWrites = (
  rxCollection: RxCollection,
  tagsCollection: RxCollection<Tag>,
  trackWrite: (write: Promise<void>) => void
): Subscription =>
  rxCollection.eventBulks$.subscribe((eventBulk) => {
    // A user-facing write rather than replication or a local-document write. Says nothing about
    // which tab performed it — see isOwnWrite.
    if (eventBulk.isLocal || !LOCAL_WRITE_CONTEXTS.includes(eventBulk.context)) return;

    const ownEvents = eventBulk.events.filter((event) => isOwnWrite(event, rxCollection.database.token));
    if (!ownEvents.length) return;

    // applyTagCounts logs its own failures and never rejects: a failed count must not tear down
    // the subscription for the rest of the session, and the tracked promise has to settle so the
    // drain in local-data-service can complete.
    const applyEvent =
      eventBulk.context === PUSH_CONFLICT_ROLLBACK_CONTEXT
        ? (event: TagCountEvent) => applyPushConflictRollback(rxCollection, tagsCollection, event)
        : (event: TagCountEvent) => applyTagCountEvent(tagsCollection, event);

    trackWrite(applyTagCounts(rxCollection.name, ownEvents, applyEvent));
  });

/**
 * Remembers, per document, the tags of the master state a conflicting push assumed, so the rollback
 * that follows can leave out whatever another device changed. RxDB emits the conflict right before
 * it writes the resolution over the fork, and only the committed rollback consumes the entry: a
 * rollback that loses to a newer local edit is retried and re-emits the conflict first.
 * @param replicationState - replication of a tagged RxCollection
 */
export const recordPushConflictsForTagCounts = (replicationState: RxReplicationState<any, any>): void => {
  const { collection } = replicationState;
  const assumedTagIds = new Map<string, string[]>();
  assumedTagIdsByCollection.set(collection, assumedTagIds);

  replicationState.conflict$.subscribe(({ input }) => {
    const documentId = (input.newDocumentState as Record<string, unknown>)[collection.schema.primaryPath] as string;
    assumedTagIds.set(documentId, countableTagIds(input.assumedMasterState));
  });
};

// Every tab must count its own writes exactly once, and `eventBulk.databaseToken` is the RECEIVING
// database's token, so it cannot tell a sibling tab's write apart; only `_rev` names the writer, as
// `<height>-<databaseInstanceToken>` (see docs/tag-counts/tag-counts-architecture.md). Any other shape counts as not ours, so an
// unrecognized revision under-counts rather than double-counts.
const isOwnWrite = (event: TagCountEvent, databaseToken: string): boolean => {
  const revision = event.documentData?._rev;

  return typeof revision === 'string' && revision.endsWith(`-${databaseToken}`);
};

// allSettled rather than all: one bulk can carry several documents, and Promise.all would hide
// every failure behind the earliest rejection. Each one is logged separately — console.error is
// the only path these reach Sentry by (console-capture integration) — and nothing is rethrown.
const applyTagCounts = async (
  collectionName: string,
  events: TagCountEvent[],
  applyEvent: (event: TagCountEvent) => Promise<unknown>
): Promise<void> => {
  const results = await Promise.allSettled(events.map(applyEvent));

  results.forEach((result) => {
    if (result.status === 'rejected') {
      console.error(`[tag-count-events] ${collectionName} tag-count update failed:`, result.reason);
    }
  });
};

// A push-conflict rollback rewrites the fork to the REAL master, so the event's own diff mixes two
// things: the inverse of this device's unpushed change (counted here, so owed) and whatever the
// master changed since the state the push assumed. The second part is never owed: either another
// device made that change and counted it, or it reached the fork through an uncounted replication
// write (the archived partition's pull, a pull whose meta write was lost) and the rollback is the
// fork onto itself. So the event diff is counted with every tag the master changed left out.
// Without a recorded conflict that part is unknown, so nothing moves (and the wiring gap is
// logged) rather than double-counting.
const applyPushConflictRollback = async (rxCollection: RxCollection, tagsCollection: RxCollection<Tag>, event: TagCountEvent) => {
  const assumedTagIdsByDocument = assumedTagIdsByCollection.get(rxCollection);
  const assumedTagIds = assumedTagIdsByDocument?.get(event.documentId);
  if (!assumedTagIds) {
    console.error(`[tag-count-events] ${rxCollection.name} push-conflict rollback without a recorded conflict, not counted`);
    return;
  }
  assumedTagIdsByDocument?.delete(event.documentId);

  const changedOnMaster = new Set(xor(assumedTagIds, countableTagIds(event.documentData)));
  const ownedHere = (doc: TagCountEvent['documentData']) => countableTagIds(doc).filter((tagId) => !changedOnMaster.has(tagId));

  return updateTagNumDocs(tagsCollection, ownedHere(event.previousDocumentData), ownedHere(event.documentData));
};

// One rule for every operation: count the tags the document owned before against the tags it owns
// after (see countableTagIds). An INSERT has no previous state and a DELETE's new state is a
// tombstone, so each reduces to a plain increment or decrement; an archive or a restore arrives as
// an UPDATE whose archived side owns nothing, and tag edits on an archived document move nothing.
const applyTagCountEvent = async (tagsCollection: RxCollection<Tag>, event: TagCountEvent) =>
  updateTagNumDocs(tagsCollection, countableTagIds(event.previousDocumentData), countableTagIds(event.documentData));

// The tags a document contributes to numDocs: none while it is archived or deleted. RxDB also
// reports a tombstone written over a tombstone as a DELETE; both sides own nothing, so the delete
// that was already counted when the document first went away is not counted again.
const countableTagIds = (doc: { tagIds?: string[]; archived?: boolean; _deleted?: boolean } | undefined): string[] => {
  if (!doc || isDeleted(doc) || isArchived(doc) || !doc.tagIds?.length) return [];
  return [...new Set(doc.tagIds)];
};

type TagCountEvent = RxChangeEventBulk<any>['events'][number];
